import gql from 'graphql-tag';
import { Media, ImageTransformationsInput } from '../../../types/media';

/**
 * Mutation variable type for uploading an image file.
 */
export type UploadImageVariables = {
  file: File;
};

/**
 * Mutation response type for uploading an image.
 */
export type UploadImageResponse = {
  uploadImage: Media;
};

/**
 * Mutation variable type for updating image transformations.
 */
export type UpdateImageTransformationsVariables = {
  path: string;
  options: ImageTransformationsInput;
};

/**
 * Mutation response type for updating image transformations.
 */
export type UpdateImageTransformationsResponse = {
  updateImageTransformations: Media;
};

/**
 * GraphQL mutation for uploading an image file to the media library.
 *
 * This mutation accepts a file and returns the uploaded media object with
 * its mediaId, path, sourceUrl, and sourceSet for use in the application.
 *
 * @example
 * ```ts
 * import { UPLOAD_IMAGE, UploadImageResponse, UploadImageVariables } from '@elevator/ui';
 *
 * const [uploadImage] = useMutation<UploadImageResponse, UploadImageVariables>(UPLOAD_IMAGE);
 *
 * await uploadImage({
 *   variables: { file: myFile }
 * });
 * ```
 */
export const UPLOAD_IMAGE = gql`
  mutation uploadImage($file: Upload!) {
    uploadImage(file: $file) {
      mediaId
      path
      sourceUrl
      sourceSet
    }
  }
`;

/**
 * GraphQL mutation for updating image transformations (e.g., cropping).
 *
 * This mutation applies transformations like cropping to an existing image
 * by its path and returns the updated media object with new URLs.
 *
 * @example
 * ```ts
 * import {
 *   UPDATE_IMAGE_TRANSFORMATIONS,
 *   UpdateImageTransformationsResponse,
 *   UpdateImageTransformationsVariables
 * } from '@elevator/ui';
 *
 * const [updateTransformations] = useMutation<
 *   UpdateImageTransformationsResponse,
 *   UpdateImageTransformationsVariables
 * >(UPDATE_IMAGE_TRANSFORMATIONS);
 *
 * await updateTransformations({
 *   variables: {
 *     path: '/path/to/image.jpg',
 *     options: { crop: 'faces' }
 *   }
 * });
 * ```
 */
export const UPDATE_IMAGE_TRANSFORMATIONS = gql`
  mutation updateImageTransformations(
    $path: String!
    $options: ImageTransformationsInput!
  ) {
    updateImageTransformations(path: $path, options: $options) {
      mediaId
      path
      sourceUrl
      sourceSet
    }
  }
`;
